<template>
    <button type="button" class="cell"
        :class="{ available, seated: !!studentName, picked, moving, pinned }"
        :aria-label="label" :title="studentName || null" :disabled="disabled"
        @click="$emit('toggle')">
        <span v-if="studentName" class="cell-name">{{ studentName }}</span>
        <Pin v-if="studentName && pinned" class="cell-pin" aria-hidden="true" />
        <Plus v-else-if="!available && !disabled" class="cell-add" aria-hidden="true" />
    </button>
</template>

<script>
import { Pin, Plus } from '@lucide/vue';

export default {
  name: 'Table',
  components: { Pin, Plus },
  props: {
    studentName: String,
    row: Number,
    col: Number,
    available: Boolean,
    // The seat the random pick landed on
    picked: Boolean,
    // The seat whose student is about to be moved
    moving: Boolean,
    // The student keeps this seat when the plan is shuffled
    pinned: Boolean,
    // Not clickable, for a field that has no table while seats are being arranged
    disabled: Boolean,
  },
  emits: ['toggle'],
  computed: {
    label() {
      const place = `Reihe ${this.row}, Platz ${this.col}`
      if (this.studentName) return `${place}: ${this.studentName}${this.pinned ? ', Platz fixiert' : ''}`
      return this.available ? `${place}: leerer Tisch` : `${place}: kein Tisch`
    }
  },
}
</script>

<style scoped>
.cell {
  container-type: size;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  padding: 0;
  border: 1px solid var(--hairline);
  border-radius: var(--r-cell);
  background: transparent;
  cursor: pointer;
  transition:
    transform var(--dur-fast) var(--ease),
    box-shadow var(--dur-fast) var(--ease),
    background-color var(--dur-fast) var(--ease);
}

.cell-add {
  width: 16px;
  height: 16px;
  stroke-width: 3;
  opacity: 0;
}

.cell:hover .cell-add,
.cell:focus-visible .cell-add {
  opacity: 1;
}

.cell:disabled {
  cursor: default;
}

.cell:not(.available):not(:disabled):hover {
  border: 2px dashed var(--ink);
  background: var(--paper);
}

/* A table is a small piece of paper on the board. */
.cell.available {
  border: var(--line);
  background: var(--paper);
  box-shadow: var(--shadow-sm);
}

.cell.available:not(.seated):hover {
  background: var(--hue-tint, var(--ground-tint));
}

.cell.available:active {
  transform: translate(2px, 2px);
  box-shadow: 0 0 0 var(--ink);
}

.cell-name {
  padding: 0 4px;
  font-size: clamp(0.6875rem, min(14cqw, 40cqh), 1.375rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.01em;
  text-align: center;
  text-wrap: balance;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

/* A fixed seat carries a pin in its corner. */
.cell-pin {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 12px;
  height: 12px;
  stroke-width: 3;
}

@container (max-width: 80px) {
  .cell-pin {
    top: 1px;
    right: 1px;
    width: 9px;
    height: 9px;
  }
}

.cell.moving {
  background: var(--ink);
  color: var(--paper);
}

/* The picked seat lifts onto a larger shadow, in the class hue. */
.cell.picked {
  background: var(--hue);
  transform: translate(-2px, -2px);
  box-shadow: var(--shadow);
  z-index: 1;
}

@media print {
  .cell {
    box-shadow: none !important;
    transform: none !important;
  }

  .cell:not(.available) {
    border-color: transparent;
  }
}
</style>
